<template>
    <Dialog header="Generate Scripts with AI" :draggable="false" v-model:visible="visible" :style="{ width: '600px' }" :modal="true" @hide="close()">
        <div class="campaign-create-text">Tell us a little about your brand, products and what you are looking for and let AI do the rest!</div>

        <div class="p-mt-4">
            <FormField
              title="Brand Tone"
              componentType="select"
              class="field-select-brand p-mb-4"
              optionLabel="name"
              optionValue="code"
              :options="state.brandToneOptions"
              v-model="aiForm.selected_brand_tone"
             />
        </div>

        <div>
            <FormField
              title="Campaign Objective"
              componentType="select"
              class="field-select-brand p-mb-4"
              optionLabel="name"
              optionValue="code"
              :options="campaignObjective.campaignObjectiveOptions"
              v-model="aiForm.objective"
            />
        </div>

        <div>
            <FormField
              v-model="aiForm.adNeeds"
              title="Describe Your Ad Needs"
              :placeholder="'We are launching a new version of our product. We are looking to create UGC style ads to run on TikTok.'"
              componentType="textarea"
              rows="5"
              col="30"
              class="p-mb-4 field-select-brand"
              :showNoteOnTop="true"
            />
        </div>

        <template #footer>
          <Button label="Generate Scripts" class="p-ml-auto next-button p-button-md p-button-footer" :loading="busy" @click.prevent="submit" />
        </template>
    </Dialog>
</template>

<script setup>
  import { defineEmits, defineProps, ref, toRef } from 'vue';
  import { useVModel } from '@vueuse/core';
  import FormField from '../../FormField.vue';
  import {useToast} from "primevue/usetoast";
  import { useForm } from '../../../hooks/useForm';
  import { useCampaign } from '../../../hooks/useCampaign';

  const toast = useToast();
  const {createScripts} = useCampaign();
  const emit = defineEmits(['update:visible']);

  const props = defineProps({
      aiForm: {type: Object, required: true},
      visible: { type: Boolean, default: false, required: true },
      campaign: {type: Object, required: true}
  });

  const state = ref({
      brandToneOptions: [
          { name: '🤗  Friendly', code: 'Friendly' },
          { name: '💎  Luxury', code: 'Luxury' },
          { name: '😌  Relaxed', code: 'Relaxed' },
          { name: '👔  Professional', code: 'Professional' },
          { name: '💪  Bold', code: 'Bold' },
          { name: '🏔️  Adventurous', code: 'Adventurous' },
          { name: '😉  Witty', code: 'Witty' },
          { name: '🤝  Persuasive', code: 'Persuasive' },
          { name: '💓  Empathetic', code: 'Empathetic' },
      ],
  });

  const campaignObjective = ref({
      campaignObjectiveOptions: [
          { name: '🚀  Brand Awareness', code: 'Brand Awareness' },
          { name: '💬  Engagement', code: 'Engagement' },
          { name: '🌐  Website Traffic', code: 'Website Traffic' },
          { name: '📋  Lead Generation', code: 'Lead Generation' },
          { name: '💰  Sales/Conversions', code: 'Sales/Conversions' },
          { name: '🔒  Customer Retention', code: 'Customer Retention' },
          { name: '📅  Event Promotion', code: 'Event Promotion' },
          { name: '🎁  Product Launch', code: 'Product Launch' },
          { name: '📰  Content Promotion', code: 'Content Promotion' },
          { name: '🏪  Store Visits', code: 'Store Visits' },
          { name: '📲  App Installs', code: 'App Installs' },
          { name: '👥  Social Media Followers', code: 'Social Media Followers' },
      ],
  });

  const visible = useVModel(props, 'visible', emit);

  const aiForm = toRef(props, 'aiForm');
  const campaign = toRef(props, 'campaign');

  const close = () => {
    aiForm.value.selected_brand_tone = null;
    aiForm.value.objective = null;
    aiForm.value.adNeeds = '';
    (visible.value = false);
  };


  const {busy, submit, reset} = useForm(async () => {
    const data = {
      product: {
        title: campaign.value.products[0]?.title,
        description: campaign.value.products[0]?.description
      },
      titles: campaign.value.scripts.map(script => script.title),
      ...aiForm.value
    };

    if (!data.selected_brand_tone || !data.objective || !data.adNeeds) {
      // SHOW ERROR TOAST
      toast.add({severity:'error', summary: 'Please fill in all the details', life: 3000});
      return;
    }

    const response = (await createScripts(data));

    campaign.value.scripts.forEach(script => {
        // Check if the title of the script exists in the response data
        if (response[script.title]) {
            // Update the need_to_say and need_to_show fields
            script.need_to_say = response[script.title].need_to_say;
            script.need_to_show = response[script.title].need_to_show;
        }
    });
    // Close the modal
    close();
  });


</script>

<style lang="scss" scoped>
@import '../../../assets/sass/theme_variables';

.help-title {
    margin-top: toRem(40);
    margin-bottom: toRem(12);
    font-weight: 600;
    font-size: toRem(18);
}

p {
    margin: 0;
    color: $textSecondaryColor;
}

img {
    margin-top: toRem(20);
}

.field-select-brand {
    display: flex;
    flex-direction: column;
}
</style>
